// Static fallback cards used on the homepage so the Moments grid + Featured
// section are NEVER empty (instant render, no race condition). Real API data is merged on top.
//
// Positioning note (Feb 2026): the homepage no longer browses listings — it
// introduces concierges. FALLBACK_CONCIERGES drives the "Meet Your Experience
// Experts" section; FALLBACK_MOMENTS drives "What's Your Moment?" (inspiration
// cards, NOT products). Pricing is intentionally never displayed at the
// moment-card level.

export const FALLBACK_MOMENTS = [
  { key: "romantic",  title: "Romance",            image: "https://images.unsplash.com/photo-1536392706976-e486e2ba97af?w=1100" },
  { key: "birthday",  title: "Celebrations",       image: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1100" },
  { key: "proposal",  title: "Proposals",          image: "https://images.unsplash.com/photo-1519741497674-611481863552?w=1100" },
  { key: "vip",       title: "VIP & Nightlife",    image: "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=1100" },
  { key: "family",    title: "Family Moments",     image: "https://images.unsplash.com/photo-1602002418082-a4443e081dd1?w=1100" },
  { key: "girls",     title: "Group Getaways",     image: "https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?w=1100" },
  { key: "corporate", title: "Company Experiences", image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1100" },
  { key: "executive", title: "Business Moments",    image: "https://images.unsplash.com/photo-1543007630-9710e4a00a20?w=1100" },
];

// Concierge profile cards — what customers see on the homepage instead of activity listings.
// Customers BOOK THE CONCIERGE, who then designs the complete experience.
// Photos: curated premium lifestyle/hospitality Unsplash portraits, face-cropped 800x800
// so each concierge reads as a real luxury Experience Expert (not a stock avatar).
//
// IMPORTANT: every entry here is `is_demo_profile: true`. These are PREVIEW
// placeholders only — they fill the homepage when fewer than 4 real approved
// Concierges exist in the DB. They MUST NOT carry trust badges (Elite /
// Identity Verified / TREL Certified / TREL+) — those only belong to real
// verified humans. ConciergeCard hides badges and disables booking when this
// flag is set, and rewrites the CTA to "Become a Concierge".
//
// Roster (4 distinct Concierge categories so customers see the marketplace
// range — pre-launch, ALL four are examples until real Concierges join):
//   1. Marcus — VIP & Entertainment Concierge
//   2. Sophia — Luxury Event Concierge
//   3. David  — Travel & Lifestyle Concierge
//   4. Ava    — Celebrations Concierge
const _PORTRAIT_PARAMS = "?w=800&h=800&fit=crop&crop=faces&auto=format&q=80";

export const FALLBACK_CONCIERGES = [
  {
    user_id: "fb-marcus",
    name: "Marcus",
    title: "VIP & Entertainment Concierge",
    city: "Miami", state: "FL",
    rating: 4.9,
    moments_created: 150,
    starting_at: 75,
    // Premium dark-suit portrait — VIP entertainment feel
    photo: `https://images.unsplash.com/photo-1519085360753-af0119f7cbe7${_PORTRAIT_PARAMS}`,
    specializes: ["Nightclubs", "VIP Tables", "Birthdays", "Corporate Events"],
    account_type: "individual",
    is_demo_profile: true,
    elite_badge_active: false,
    identity_verified: false,
    trel_certified: false,
    trel_plus_active: false,
    _fallback: true,
  },
  {
    user_id: "fb-dc-elite",
    name: "DC Elite Experiences",
    title: "VIP & Corporate Experiences",
    city: "Washington", state: "DC",
    rating: 4.9,
    moments_created: 850,
    starting_at: 1200,
    // Business cover image — bright luxury corporate event scene
    photo: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&h=800&fit=crop&auto=format&q=80",
    specializes: ["VIP Access", "Corporate Events", "Private Parties", "Luxury Transportation"],
    account_type: "business",
    is_demo_profile: true,
    elite_badge_active: false,
    identity_verified: false,
    trel_certified: false,
    trel_plus_active: false,
    _fallback: true,
  },
  {
    user_id: "fb-sophia",
    name: "Sophia",
    title: "Luxury Event Concierge",
    city: "New York", state: "NY",
    rating: 5.0,
    moments_created: 132,
    starting_at: 150,
    // Elegant event planner — refined, soft-light portrait
    photo: `https://images.unsplash.com/photo-1487412720507-e7ab37603c6f${_PORTRAIT_PARAMS}`,
    specializes: ["Private Dining", "Executive Events", "Anniversaries", "Premium Experiences"],
    account_type: "individual",
    is_demo_profile: true,
    elite_badge_active: false,
    identity_verified: false,
    trel_certified: false,
    trel_plus_active: false,
    _fallback: true,
  },
  {
    user_id: "fb-vegas-nightlife",
    name: "Vegas Nightlife Co",
    title: "Nightlife & VIP Experiences",
    city: "Las Vegas", state: "NV",
    rating: 4.8,
    moments_created: 640,
    starting_at: 75,
    // Vegas nightlife cinematic shot
    photo: "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=800&h=800&fit=crop&auto=format&q=80",
    specializes: ["Nightclub VIP", "Bottle Service", "Bachelor & Bachelorette", "Pool Day Experiences"],
    account_type: "business",
    is_demo_profile: true,
    elite_badge_active: false,
    identity_verified: false,
    trel_certified: false,
    trel_plus_active: false,
    _fallback: true,
  },
  {
    user_id: "fb-david",
    name: "David",
    title: "Travel & Lifestyle Concierge",
    city: "Los Angeles", state: "CA",
    rating: 4.9,
    moments_created: 98,
    starting_at: 49,
    // Travel/adventure pro — outdoor, approachable, global
    photo: `https://images.unsplash.com/photo-1492562080023-ab3db95bfbce${_PORTRAIT_PARAMS}`,
    specializes: ["Travel Planning", "Group Trips", "Company Retreats"],
    account_type: "individual",
    is_demo_profile: true,
    elite_badge_active: false,
    identity_verified: false,
    trel_certified: false,
    trel_plus_active: false,
    _fallback: true,
  },
  {
    user_id: "fb-ava",
    name: "Ava",
    title: "Celebrations Concierge",
    city: "Las Vegas", state: "NV",
    rating: 4.9,
    moments_created: 115,
    starting_at: 600,
    // Celebrations / lifestyle — warm, bright, social
    photo: `https://images.unsplash.com/photo-1554151228-14d9def656e4${_PORTRAIT_PARAMS}`,
    specializes: ["Birthdays", "Bachelorette", "Bottle Service", "Day Parties"],
    account_type: "individual",
    is_demo_profile: true,
    elite_badge_active: false,
    identity_verified: false,
    trel_certified: false,
    trel_plus_active: false,
    _fallback: true,
  },
];

// Legacy export — Explore page and some links still expect it. Kept as a thin
// re-export so we don't break callers. Homepage no longer uses this list.
export const FALLBACK_EXPERIENCES = [
  {
    experience_id: "fb-chef-miami",
    title: "Private Chef Dinner",
    city: "Miami", state: "FL", duration: "3 hours",
    price: 450, rating: 4.9,
    images: ["https://images.unsplash.com/photo-1536392706976-e486e2ba97af?w=1100"],
    provider: { verification_status: "Approved", trel_plus_active: true, identity_verified: true, trel_certified: true },
    _fallback: true,
  },
  {
    experience_id: "fb-bday-vegas",
    title: "Birthday Setup Experience",
    city: "Las Vegas", state: "NV", duration: "2 hours",
    price: 320, rating: 4.8,
    images: ["https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1100"],
    provider: { verification_status: "Approved", identity_verified: true, trel_certified: true },
    _fallback: true,
  },
  {
    experience_id: "fb-proposal-nyc",
    title: "Romantic Proposal Planning",
    city: "New York", state: "NY", duration: "Full evening",
    price: 1200, rating: 5.0,
    images: ["https://images.unsplash.com/photo-1519741497674-611481863552?w=1100"],
    provider: { verification_status: "Approved", trel_plus_active: true, identity_verified: true, trel_certified: true, elite_badge_active: true },
    _fallback: true,
  },
  {
    experience_id: "fb-vip-la",
    title: "Airport VIP Arrival",
    city: "Los Angeles", state: "CA", duration: "1 hour",
    price: 280, rating: 4.8,
    images: ["https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1100"],
    provider: { verification_status: "Approved", identity_verified: true, trel_certified: true },
    _fallback: true,
  },
  {
    experience_id: "fb-family-orlando",
    title: "Family Adventure Day",
    city: "Orlando", state: "FL", duration: "6 hours",
    price: 220, rating: 4.9,
    images: ["https://images.unsplash.com/photo-1602002418082-a4443e081dd1?w=1100"],
    provider: { verification_status: "Approved", identity_verified: true, trel_certified: true },
    _fallback: true,
  },
  {
    experience_id: "fb-wellness-scottsdale",
    title: "Wellness Spa Experience",
    city: "Scottsdale", state: "AZ", duration: "3 hours",
    price: 200, rating: 4.9,
    images: ["https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=1100"],
    provider: { verification_status: "Approved", trel_plus_active: true, identity_verified: true, trel_certified: true },
    _fallback: true,
  },
];

// Merge real listings first, then fill remaining slots with fallbacks (real first, demo fills gaps).
export function mergeWithFallback(real, fallback, target = 8) {
  const out = Array.isArray(real) ? [...real] : [];
  if (out.length >= target) return out.slice(0, target);
  const have = new Set(out.map((x) => `${x.title || x.name}|${x.city}`));
  for (const f of fallback) {
    if (out.length >= target) break;
    if (!have.has(`${f.title || f.name}|${f.city}`)) out.push(f);
  }
  return out.slice(0, target);
}
